import {
  queryOptions,
  type UseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

export type AutoApproveDlnPositionsResponse = boolean | null;

/**
 * Fetches auto-approve DLN positions setting for a company from the API.
 * @param companyId - The ID of the company to fetch settings for.
 * @returns Whether or not to auto-approve DLN positions.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_auto_approve_dln_positions_company__company_id__auto_approve_dln_positions_get
 */
export const fetchCompanyAutoApproveDlnPositions = async (companyId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.COMPANY.GET_AUTO_APPROVE_DLN_POSITIONS(companyId))
    .json<AutoApproveDlnPositionsResponse>();

  return response;
};

/**
 * Generates the query options for the company auto-approve DLN positions query.
 *
 * @param companyId - The ID of the company to fetch settings for.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const companyAutoApproveDlnPositionsQueryOptions = <
  TData = AutoApproveDlnPositionsResponse,
>({
  companyId,
  options,
}: {
  companyId: UUID;
  options?: Omit<
    UseQueryOptions<AutoApproveDlnPositionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: async () => fetchCompanyAutoApproveDlnPositions(companyId),
    queryKey: queryKeysCompany.getAutoApproveDlnPositions(companyId),
    staleTime: ms('5m'),
    ...options,
  });

/**
 * React Query based custom hook for getting auto-approve DLN positions setting for a single company.
 * @param companyId - Company ID to fetch settings for.
 * @param options - Additional options for the useQuery hook.
 * @returns Query result for the company.
 */
export const useQueryCompanyAutoApproveDlnPositions = (
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<AutoApproveDlnPositionsResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useSuspenseQuery(
    companyAutoApproveDlnPositionsQueryOptions({ companyId, options }),
  );
